/**
 * 离线排队（R2-4，设计稿 S05 的「等上传」承诺）：
 * 断网时写备忘先存本机（IndexedDB；不可用时退化为内存——那一档
 * 只保当前页面会话，关了标签就没了，但比直接丢好），网络回来自动补传。
 *
 * 排队的是**文字 + 附件**整条（File 对象可直接进 IndexedDB）。
 * 语音不排队：录音本身依赖在线上传链路，断网时录音入口本来就用不了。
 *
 * 补传不带 expected_version——排队期间这条在服务器上不存在，无从冲突。
 */

export interface QueuedMemo {
  id: string
  /** 作用域：null=杂项；项目页写的带项目 id。 */
  projectId: string | null
  body: string
  /** 附件：File 原样进队（类型/大小已在入队前拦过一道）。 */
  files: File[]
  createdAt: number
}

const DB_NAME = 'npc-memo-queue'
const STORE = 'queue'

/** 内存兜底：jsdom / 隐私模式 / IndexedDB 被禁的环境。 */
const memory = new Map<string, QueuedMemo>()

let dbPromise: Promise<IDBDatabase | null> | null = null

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1)
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE, { keyPath: 'id' })
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
  return dbPromise
}

async function withStore(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest,
): Promise<unknown> {
  const db = await openDb()
  if (!db) return null
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const req = fn(tx.objectStore(STORE))
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export async function enqueueMemo(item: QueuedMemo): Promise<void> {
  memory.set(item.id, item)
  try {
    await withStore('readwrite', (store) => store.put(item))
  } catch {
    // IDB 写失败（配满/私密）——内存那一份还在，本会话内仍会补传
  }
}

export async function queueAll(): Promise<QueuedMemo[]> {
  try {
    const rows = (await withStore('readonly', (store) => store.getAll())) as
      | QueuedMemo[]
      | null
    if (rows && rows.length > 0) {
      // 以 IDB 为准同步进内存视图（跨标签写的也能看见）
      memory.clear()
      for (const row of rows) memory.set(row.id, row)
    }
  } catch {
    // 读不了就用内存那份
  }
  return [...memory.values()].sort((a, b) => a.createdAt - b.createdAt)
}

export async function dequeueMemo(id: string): Promise<void> {
  memory.delete(id)
  try {
    await withStore('readwrite', (store) => store.delete(id))
  } catch {
    // 同上：内存为准
  }
}

type Listener = () => void
const listeners = new Set<Listener>()

/** 队列变化（入队/出队/补传完成）时通知列表刷新。 */
export function onQueueChange(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

function notify() {
  for (const fn of listeners) fn()
}

/** 一次入队 + 通知。 */
export async function enqueueAndNotify(item: QueuedMemo): Promise<void> {
  await enqueueMemo(item)
  notify()
}

/** 一次出队 + 通知。 */
export async function dequeueAndNotify(id: string): Promise<void> {
  await dequeueMemo(id)
  notify()
}

/**
 * 补传：把队列逐条走「建条目 → 传附件」的在线流程。
 * 任何一条网络失败就停下（还在断网），剩下的等下一次 online。
 * 返回本次成功条数。
 */
/**
 * 补传（巡检修复：毒丸）。失败分两种：
 * - 断网（navigator.onLine 为假）→ 停下保序，等下一次 online——这是正常排队语义；
 * - **在线仍失败**（项目被删/没了权限等 4xx）→ 这是死单：出队并上报，
 *   继续补后面的——否则第一条死单永远排第一，整个队列永久卡死。
 */
export async function flushQueue(
  create: (item: QueuedMemo) => Promise<void>,
  onDead?: (item: QueuedMemo) => void,
): Promise<{ done: number; dead: QueuedMemo[] }> {
  const items = await queueAll()
  let done = 0
  const dead: QueuedMemo[] = []
  for (const item of items) {
    try {
      await create(item)
      await dequeueMemo(item.id)
      done += 1
    } catch {
      if (typeof navigator !== 'undefined' && !navigator.onLine) break
      await dequeueMemo(item.id)
      dead.push(item)
      onDead?.(item)
    }
  }
  if (done > 0 || dead.length > 0) notify()
  return { done, dead }
}
